// src/pages/Dashboard/Admin/Articles/components/ArticleFormDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";

import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

import {
  ARTICLE_FALLBACK_IMAGE,
  getDefaultArticleForm,
  mapArticleToForm,
} from "../utils/articlesDashboardUtils";

function DrawerField({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ToggleLine({ id, name, checked, onChange, label, description }) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800"
    >
      <input
        id={id}
        name={name}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
      />

      <span>
        <span className="block text-sm font-bold text-slate-900 dark:text-white">
          {label}
        </span>

        {description ? (
          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export default function ArticleFormDrawer({
  isOpen,
  mode,
  article,
  categories,
  nextOrder,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultArticleForm(nextOrder));

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit") {
      setFormData(mapArticleToForm(article, nextOrder));
    } else {
      setFormData(getDefaultArticleForm(nextOrder));
    }
  }, [isOpen, mode, article, nextOrder]);

  const title = mode === "edit" ? "Modifier un article" : "Ajouter un article";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Créer l’article";

  const visibleCategories = useMemo(() => {
    const current = String(formData.categorie || "").trim();

    return Array.from(
      new Set([
        ...categories,
        ...(current && current !== "_new" ? [current] : []),
      ]),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [categories, formData.categorie]);

  const finalCategory =
    formData.categorie === "_new"
      ? String(formData.newCategorie || "").trim()
      : String(formData.categorie || "").trim();

  const canSubmit =
    String(formData.nom || "").trim() &&
    String(formData.prix ?? "").trim() !== "" &&
    finalCategory &&
    !submitting;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.(formData);
  };

  if (!isOpen) return null;

  const previewUrl =
    toAbsoluteArticlePhotoUrl(formData.photo_url) || ARTICLE_FALLBACK_IMAGE;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Catalogue
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez les informations principales de l’article, sa
              catégorie, son stock et sa visibilité côté boutique.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-4 sm:grid-cols-[160px,1fr]">
              <div>
                <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <img
                    src={previewUrl}
                    alt={formData.nom || "Article"}
                    className="h-40 w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.src = ARTICLE_FALLBACK_IMAGE;
                    }}
                  />
                </div>

                <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
                  L’image peut aussi être téléversée depuis le tableau.
                </p>
              </div>

              <div className="space-y-4">
                <DrawerField label="Nom de l’article *">
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Ex : T-shirt club"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>

                <DrawerField label="Description">
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Description courte de l’article..."
                    className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <DrawerField label="Prix (€) *">
                <input
                  type="number"
                  step="0.01"
                  name="prix"
                  value={formData.prix}
                  onChange={handleChange}
                  required
                  placeholder="Ex : 12.50"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>

              <DrawerField label="Taille">
                <input
                  type="text"
                  name="taille"
                  value={formData.taille}
                  onChange={handleChange}
                  placeholder="S, M, L, 150, 160..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <DrawerField label="Catégorie *">
                <select
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Choisir une catégorie —</option>

                  {visibleCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}

                  <option value="_new">+ Créer une nouvelle catégorie</option>
                </select>
              </DrawerField>

              <DrawerField label="Ordre d’affichage">
                <input
                  type="number"
                  name="ordre_article"
                  value={formData.ordre_article}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>
            </div>

            {formData.categorie === "_new" ? (
              <DrawerField label="Nouvelle catégorie *">
                <input
                  type="text"
                  value={formData.newCategorie}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      newCategorie: event.target.value,
                    }))
                  }
                  placeholder="Ex : Boutique, Licence, Cotisation..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <ToggleLine
                id="article-active"
                name="is_active"
                checked={!!formData.is_active}
                onChange={handleChange}
                label="Afficher sur la boutique"
                description="L’article sera visible côté adhérent ou boutique publique."
              />

              <ToggleLine
                id="article-stockable"
                name="is_stockable"
                checked={!!formData.is_stockable}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_stockable: event.target.checked,
                    quantite_stock: event.target.checked
                      ? prev.quantite_stock
                      : 0,
                  }))
                }
                label="Gestion du stock"
                description="Activez cette option pour suivre la quantité disponible."
              />
            </div>

            <DrawerField label="Quantité en stock">
              <input
                type="number"
                name="quantite_stock"
                value={formData.quantite_stock}
                onChange={handleChange}
                disabled={!formData.is_stockable}
                placeholder="Ex : 25"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:disabled:bg-slate-800"
              />
            </DrawerField>

            <DrawerField
              label="URL de la photo"
              hint="Vous pouvez coller une URL ou téléverser l’image après création depuis le tableau."
            >
              <input
                type="text"
                name="photo_url"
                value={formData.photo_url}
                onChange={handleChange}
                placeholder="https://... ou /uploads/..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}
